import { useEffect, useMemo, useState } from 'react';

import '../../../estilos/calendario-plegado.css';
import { formatearFecha } from '../../../nucleo/fecha/fechaLocal.js';
import { importe as formatearImporte, UMBRAL_DE_PLIEGUE_POR_ANIO } from '../formato.js';

/**
 * EL CALENDARIO DE UN PLAN LARGO, PLEGADO POR AÑO · PIEZA COMPARTIDA ÚNICA
 * (incremento 22/22; RF-274, RN-767, RN-771, RN-772, RNF-142; prototipo aprobado
 * v45 `-plan-largo`).
 *
 * POR QUÉ ESTE COMPONENTE Y NO TRES PLIEGUES PARECIDOS: el calendario se pinta en
 * TRES pantallas —las mensualidades del plan, el desglose de la simulación y la
 * vista previa del alta en curso—. Si cada una pliega a su manera, la próxima regla
 * del renglón-resumen se aplica en una y se olvida en las otras dos. Aquí hay UNA
 * implementación; las pantallas la consumen pasando sus renglones ya resueltos.
 *
 * EL PLIEGUE ES PRESENTACIÓN, NO DATO (D-451): el servidor sigue entregando el
 * calendario completo en una sola respuesta, con sus estados, sus importes y su
 * contador «n/m» YA RESUELTOS (RNF-038). Aquí no se concatena ningún contador, no
 * se divide ningún importe y no se calcula ninguna fecha: lo único que se hace es
 * AGRUPAR por el año del corte y SUMAR lo que ese año pliega, que es exactamente lo
 * que RN-767 pide enseñar en el renglón cerrado.
 *
 * LAS CINCO REGLAS QUE ESTE COMPONENTE HACE CUMPLIR:
 *
 *   1. UMBRAL (RN-767) · Con 24 mensualidades o menos la lista se pinta COMPLETA,
 *      igual que siempre. El pliegue aparece a partir de 25.
 *
 *   2. VIENE ABIERTO SOLO EL AÑO DE LA QUE SIGUE (RN-767) · o el primero, si ya no
 *      queda ninguna por caer. Desplegar el plan completo de un golpe es justo lo
 *      que RNF-142 evita, así que NO EXISTE «abrir todos».
 *
 *   3. NO SE RECUERDA NADA ENTRE VISITAS (RN-771) · El estado de años abiertos vive
 *      mientras la pantalla está abierta. No se guarda en el dispositivo ni viaja
 *      al servidor: recordar un año viejo escondería la que sigue conforme el plan
 *      avanza. Al volver a entrar se vuelve a derivar.
 *
 *   4. LAS HISTÓRICAS CUENTAN JUNTO CON LAS CARGADAS (RN-772) · En el
 *      renglón-resumen una mensualidad que el banco cargó antes está cargada por
 *      construcción. La DISTINCIÓN no se pierde: al abrir el año, cada renglón la
 *      pinta con la pieza compartida del contador, que es quien la dibuja.
 *
 *   5. OPERABLE A 320 px (RNF-142) · Solo los años abiertos pintan renglones, así
 *      que el DOM visible queda acotado por el pliegue; el renglón del año es un
 *      botón de 48 px de alto sin tabla y sin anchos fijos.
 *
 * FORMA DE CADA RENGLÓN QUE RECIBE (lo arma la pantalla con lo que vino del
 * servidor, sin inventar nada):
 *   clave          · identificador estable para la lista
 *   fechaCorte     · «AAAA-MM-DD» tal como viaja; de aquí sale el año del grupo
 *   importe        · cadena decimal tal como viaja (RNF-001)
 *   estaCargada    · ya cargada, por fecha o por histórica, o liquidada
 *   esHistorica    · la cargó el banco antes de usar la aplicación
 *   esLaQueSigue   · la próxima no liquidada (como máximo una en todo el plan)
 *   esLaDelResiduo · lleva los centavos que sobran (RN-100)
 */
export function CalendarioPlegadoPorAnio({
  renglones,
  codigoISO,
  pintarRenglon,
  datoDePrueba = 'calendario-plegado',
  /*
   * El estado agregado del año se enseña donde hay algo que agregar: en el plan
   * guardado y en la vista previa del alta en curso, que ya traen mensualidades
   * cargadas. En el desglose de un plan que todavía no existe, TODO está por caer
   * y el prototipo aprobado no lo pinta: un renglón que dice siempre lo mismo deja
   * de leerse.
   */
  mostrarEstadoAgregado = true,
  /*
   * [ADENDA · incremento 20/21 · RN-776] DOS PROPIEDADES OPCIONALES, y las dos
   * conservan EXACTAMENTE la conducta aprobada del 22 cuando no se pasan.
   *
   * POR QUE SE AJUSTA LA PIEZA Y NO SE HACE UNA VARIANTE LOCAL: porque RN-776 lo
   * manda así —«si la pieza necesitara un ajuste para este consumidor, hacerlo EN
   * LA PIEZA, nunca una variante local»— y porque una segunda implementación de
   * pliegue es defecto de construcción aunque se vea igual.
   *
   * QUE NECESITABA EL PRESTAMO: su renglón de año habla de PAGOS, no de
   * mensualidades, y su estado agregado se dice con otras palabras («Todos por
   * pagar», «2 pagados · 10 por pagar») porque en un préstamo «cargada» no es la
   * palabra de la titular: ella paga. El criterio del pliegue, el umbral, el año
   * que abre, la ausencia de «abrir todos» y la falta de memoria entre visitas NO
   * CAMBIAN NI UNA LINEA (RN-767, RN-771).
   */
  sustantivo = { singular: 'mensualidad', plural: 'mensualidades' },
  resumirEstadoAgregado = textoDelEstadoAgregado,
}) {
  const grupos = useMemo(() => agruparPorAnio(renglones), [renglones]);
  const anioQueAbre = useMemo(() => derivarAnioQueAbre(grupos), [grupos]);

  const [abiertos, setAbiertos] = useState(() => (anioQueAbre === null ? {} : { [anioQueAbre]: true }));

  /*
   * RN-771 · el estado inicial SE DERIVA DE NUEVO cuando cambia el plan que se está
   * pintando (otra consulta, otra simulación): no se arrastra el año que estaba
   * abierto para un calendario que ya no es el mismo.
   */
  useEffect(() => {
    setAbiertos(anioQueAbre === null ? {} : { [anioQueAbre]: true });
  }, [anioQueAbre, grupos.length]);

  const pliega = renglones.length > UMBRAL_DE_PLIEGUE_POR_ANIO;

  if (!pliega) {
    return (
      <div className="lista" data-prueba={datoDePrueba} data-plegado="no">
        {renglones.map((renglon) => pintarRenglon(renglon))}
      </div>
    );
  }

  return (
    <div className="calendario-plegado" data-prueba={datoDePrueba} data-plegado="si">
      {grupos.map((grupo) => {
        const abierto = Boolean(abiertos[grupo.anio]);

        return (
          <div className="anio-del-plan" key={grupo.anio} data-prueba={`${datoDePrueba}-grupo-${grupo.anio}`}>
            <button
              type="button"
              className={`anio-resumen${abierto ? ' abierto' : ''}${grupo.traeLaQueSigue ? ' con-la-que-sigue' : ''}`}
              aria-expanded={abierto}
              onClick={() => setAbiertos((previos) => ({ ...previos, [grupo.anio]: !previos[grupo.anio] }))}
              data-prueba={`${datoDePrueba}-anio-${grupo.anio}`}
            >
              <span className="anio-numero">{grupo.anio}</span>

              <span className="anio-medio">
                <b>
                  {grupo.cuantas} {grupo.cuantas === 1 ? sustantivo.singular : sustantivo.plural}
                  {' · '}
                  {formatearImporte(grupo.importeDeCadaUna, codigoISO)} cada una
                </b>

                <small>
                  {mostrarEstadoAgregado && (
                    <span className={`chip${grupo.todasCargadas ? ' ok' : ''}`} data-prueba={`${datoDePrueba}-estado-${grupo.anio}`}>
                      {resumirEstadoAgregado(grupo)}
                    </span>
                  )}
                  {grupo.traeElResiduo && (
                    <span className="chip" data-prueba={`${datoDePrueba}-residuo-${grupo.anio}`}>
                      la última, {formatearImporte(grupo.importeDelUltimo, codigoISO)}
                    </span>
                  )}
                  {grupo.traeLaQueSigue && (
                    <span className="chip" data-prueba={`${datoDePrueba}-la-que-sigue-${grupo.anio}`}>
                      aquí está la que sigue
                    </span>
                  )}
                </small>

                {/* Cerrado: cuánto suma el año. Abierto: el rango de cortes, porque
                    con los renglones a la vista la suma ya es ruido (RN-767). */}
                <small data-prueba={`${datoDePrueba}-pie-${grupo.anio}`}>
                  {abierto
                    ? `Del ${formatearFecha(grupo.primerCorte)} al ${formatearFecha(grupo.ultimoCorte)}`
                    : `Suman ${formatearImporte(grupo.importeDelAnio, codigoISO)} en el año`}
                </small>
              </span>

              <span className="anio-flecha" aria-hidden="true">{abierto ? '▾' : '▸'}</span>
            </button>

            {abierto && (
              <div className="anio-cuerpo lista" data-prueba={`${datoDePrueba}-cuerpo-${grupo.anio}`}>
                {grupo.renglones.map((renglon) => pintarRenglon(renglon))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** El año del corte, tomado de la fecha que YA viene resuelta del servidor. */
function anioDe(renglon) {
  return String(renglon.fechaCorte).slice(0, 4);
}

/**
 * Suma de importes EN CENTAVOS ENTEROS, nunca con coma flotante acumulada: es el
 * mismo criterio con el que el servidor reparte (RNF-033). Aquí no se divide nada;
 * solo se suma lo que ya está resuelto.
 */
function sumarImportes(valores) {
  const centavos = valores.reduce((acumulado, valor) => acumulado + Math.round(Number(valor) * 100), 0);
  return (centavos / 100).toFixed(2);
}

function agruparPorAnio(renglones) {
  const porAnio = new Map();

  renglones.forEach((renglon) => {
    const anio = anioDe(renglon);
    if (!porAnio.has(anio)) porAnio.set(anio, []);
    porAnio.get(anio).push(renglon);
  });

  return [...porAnio.entries()].map(([anio, delAnio]) => {
    // RN-772: históricas y cargadas en la aplicación CUENTAN JUNTAS.
    const cargadas = delAnio.filter((r) => r.estaCargada || r.esHistorica).length;

    return {
      anio,
      renglones: delAnio,
      cuantas: delAnio.length,
      cargadas,
      porCaer: delAnio.length - cargadas,
      todasCargadas: cargadas === delAnio.length,
      todasPorCaer: cargadas === 0,
      traeLaQueSigue: delAnio.some((r) => r.esLaQueSigue),
      traeElResiduo: delAnio.some((r) => r.esLaDelResiduo),
      importeDeCadaUna: delAnio[0].importe,
      importeDelUltimo: delAnio[delAnio.length - 1].importe,
      importeDelAnio: sumarImportes(delAnio.map((r) => r.importe)),
      primerCorte: delAnio[0].fechaCorte,
      ultimoCorte: delAnio[delAnio.length - 1].fechaCorte,
    };
  });
}

/** RN-767 · el año de la próxima no liquidada; el primero, si ya no queda ninguna. */
function derivarAnioQueAbre(grupos) {
  if (grupos.length === 0) return null;
  return (grupos.find((grupo) => grupo.traeLaQueSigue) ?? grupos[0]).anio;
}

/** Los tres literales de RN-767, y no hay un cuarto. */
function textoDelEstadoAgregado(grupo) {
  if (grupo.todasCargadas) return 'Todas cargadas';
  if (grupo.todasPorCaer) return 'Todas por caer';
  return `${grupo.cargadas} cargadas · ${grupo.porCaer} por caer`;
}
